// useAgentActionQueue — a real, read + decide view of
// sq_agent_action_requests across every Agent Workspace the operator can
// see. Reuses the exact endpoints Operations Agent Command's read-only
// queue already reads (api.agentWorkspace.actionRequests.list) plus the
// canonical decision endpoint (api.agentWorkspace.approve) — no second
// queue, no fabricated rows, no client-side approval logic.
//
// Self-approval denial and the atomic pending_approval -> decided
// transition are enforced SERVER-SIDE (api/square/agent-workspace/
// approve.ts, hardened in PR #129). This hook only classifies the
// *shape* of a failure response so the UI can show the operator the
// real reason — it never re-implements the rule as the only guard, and
// it never hides or reinterprets the server's own wording.
//
// Distinguishing the two possible 403s from approve.ts:
//   - insufficient role level (minLevel 80 gate, applied by withAuth
//     BEFORE the handler body runs): jsonError(res, 403, 'forbidden', …)
//     -> payload.error = 'forbidden' -> ApiError.code === 'forbidden'.
//   - self-decision denial (the handler's own check): the route returns
//     `{ reason: 'self_decision_forbidden', message }` — no `error`
//     field, so apiRequest's code derivation falls back to the generic
//     `http_403`. There is no other 403 this route can return, so any
//     403 whose code isn't the generic 'forbidden' string IS the
//     self-decision denial; its `message` is the server's exact text.

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, isAuthError, isForbiddenError } from '../../../api/client'
import type { AgentWorkspaceSummary } from './useAgentWorkspaces'

export interface AgentActionRequestRow {
  id: string
  workspace_id: string
  workspace_name: string
  title: string
  summary: string
  action_type: string
  risk_tier: string
  approval_status: string
  execution_status: string
  prepared_by_agent_id: string | null
  requested_by: string | null
  approved_by: string | null
  approved_at: string | null
  rejection_reason: string | null
  created_at: string
}

export type QueueLoadState = 'loading' | 'ready' | 'auth_required' | 'forbidden' | 'error'

export type DecisionStatus =
  | 'idle'
  | 'deciding'
  | 'self_decision_forbidden'
  | 'insufficient_level'
  | 'already_decided'
  | 'error'

export interface DecisionState {
  status: DecisionStatus
  message: string | null
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) return !err.message || err.message === err.code ? err.code : `${err.code}: ${err.message}`
  if (err instanceof Error) return err.message || 'request_failed'
  return String(err)
}

function normalizeRow(row: Record<string, unknown>, workspace: AgentWorkspaceSummary): AgentActionRequestRow {
  return {
    id: String(row.id ?? ''),
    workspace_id: String(row.workspace_id ?? workspace.id),
    workspace_name: workspace.name,
    title: String(row.title ?? 'Untitled request'),
    summary: String(row.summary ?? ''),
    action_type: String(row.action_type ?? ''),
    risk_tier: String(row.risk_tier ?? 'medium'),
    approval_status: String(row.approval_status ?? 'pending_approval'),
    execution_status: String(row.execution_status ?? 'not_started'),
    prepared_by_agent_id: typeof row.prepared_by_agent_id === 'string' ? row.prepared_by_agent_id : null,
    requested_by: typeof row.requested_by === 'string' ? row.requested_by : null,
    approved_by: typeof row.approved_by === 'string' ? row.approved_by : null,
    approved_at: typeof row.approved_at === 'string' ? row.approved_at : null,
    rejection_reason: typeof row.rejection_reason === 'string' ? row.rejection_reason : null,
    created_at: String(row.created_at ?? ''),
  }
}

export function useAgentActionQueue(workspaces: AgentWorkspaceSummary[], workspacesReady: boolean) {
  const [state, setState] = useState<QueueLoadState>('loading')
  const [rows, setRows] = useState<AgentActionRequestRow[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [decisions, setDecisions] = useState<Record<string, DecisionState>>({})

  const load = useCallback(async () => {
    if (!workspacesReady) return
    if (workspaces.length === 0) {
      setRows([])
      setState('ready')
      return
    }
    setState('loading')
    setErrorMessage(null)
    try {
      const perWorkspace = await Promise.all(
        workspaces.map((w) =>
          api.agentWorkspace.actionRequests
            .list(w.id)
            .then((r) => (r.action_requests || []).map((row) => normalizeRow(row, w))),
        ),
      )
      const flat = perWorkspace.flat().sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      setRows(flat)
      setState('ready')
    } catch (err) {
      if (isAuthError(err)) return setState('auth_required')
      if (isForbiddenError(err)) return setState('forbidden')
      setErrorMessage(formatError(err))
      setState('error')
    }
  }, [workspaces, workspacesReady])

  useEffect(() => {
    void load()
  }, [load])

  const decide = useCallback(
    async (requestId: string, decision: 'approved' | 'rejected', note?: string) => {
      setDecisions((d) => ({ ...d, [requestId]: { status: 'deciding', message: null } }))
      try {
        const result = await api.agentWorkspace.approve(requestId, decision, note)
        setDecisions((d) => ({ ...d, [requestId]: { status: 'idle', message: result.message ?? null } }))
        await load()
        return result
      } catch (err) {
        if (err instanceof ApiError && err.status === 403 && err.code === 'forbidden') {
          setDecisions((d) => ({ ...d, [requestId]: { status: 'insufficient_level', message: err.message } }))
        } else if (err instanceof ApiError && err.status === 403) {
          setDecisions((d) => ({ ...d, [requestId]: { status: 'self_decision_forbidden', message: err.message } }))
        } else if (err instanceof ApiError && err.status === 409) {
          setDecisions((d) => ({
            ...d,
            [requestId]: {
              status: 'already_decided',
              message: 'Someone else already decided this request — reloading the current state.',
            },
          }))
          await load()
        } else {
          setDecisions((d) => ({ ...d, [requestId]: { status: 'error', message: formatError(err) } }))
        }
        return null
      }
    },
    [load],
  )

  return { state, rows, errorMessage, decisions, decide, reload: load }
}
